<script lang="ts">
  import { untrack } from 'svelte';
  import Dialog from './Dialog.svelte';
  import Icon from './Icon.svelte';
  import { setPoiGroupMembership } from './api';
  import type { GroupView, PoiView } from '~/shared/api.js';

  let {
    tripToken,
    poi,
    groups,
    onClose,
    onUpdated,
    onCreateGroup,
  }: {
    tripToken: string;
    poi: PoiView;
    groups: GroupView[];
    onClose: () => void;
    onUpdated: (updatedPoi: PoiView) => void;
    onCreateGroup: () => void;
  } = $props();

  let selected: Set<number> = $state(new Set(untrack(() => poi.groupIds)));
  let saving: boolean = $state(false);
  let error: string | null = $state(null);

  async function toggle(groupId: number) {
    const next = new Set(selected);
    if (next.has(groupId)) next.delete(groupId);
    else next.add(groupId);
    selected = next;
    await save();
  }

  async function save() {
    saving = true;
    error = null;
    try {
      const updated = await setPoiGroupMembership(tripToken, poi.id, [...selected]);
      onUpdated(updated);
    } catch (e) {
      error = e instanceof Error ? e.message : 'Could not save groups';
    } finally {
      saving = false;
    }
  }
</script>

<Dialog {onClose} variant="sheet">
  <div class="sheet" role="dialog" aria-modal="true" aria-label="Edit groups">
    <header class="head">
      <div class="heading">
        <span class="caption">Groups for</span>
        <h2 class="truncate">{poi.name}</h2>
      </div>
      <button class="close-btn" type="button" aria-label="Close" onclick={onClose}>
        <Icon name="x" size={18} />
      </button>
    </header>

    {#if groups.length === 0}
      <p class="empty">No groups yet. Create one to organise your places.</p>
    {:else}
      <div class="chips">
        {#each groups as g (g.id)}
          {@const active = selected.has(g.id)}
          <button
            type="button"
            class="chip"
            class:active
            aria-pressed={active}
            disabled={saving}
            onclick={() => toggle(g.id)}
          >
            <span class="dot dot--{g.tint}"></span>
            <span class="label">{g.name}</span>
          </button>
        {/each}
      </div>
    {/if}

    {#if error}
      <p class="err">{error}</p>
    {/if}

    <button class="btn btn-tertiary new-group-btn" type="button" onclick={onCreateGroup}>
      <Icon name="plus" size={14} />
      <span>Create new group</span>
    </button>
  </div>
</Dialog>

<style>
  .sheet {
    position: relative;
    background: var(--color-canvas);
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
    padding: 1.5rem;
    padding-bottom: 1.75rem;
    width: 100%;
    max-width: 540px;
    box-shadow: rgba(15, 15, 15, 0.2) 0 -8px 32px -4px;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  @media (min-width: 768px) {
    .sheet { border-radius: var(--radius-md); }
  }
  .head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
  }
  .heading { display: flex; flex-direction: column; min-width: 0; }
  .caption {
    font-size: 12px;
    color: var(--color-muted);
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .heading h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: var(--color-ink);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .close-btn {
    background: transparent;
    border: 0;
    width: 32px;
    height: 32px;
    border-radius: 9999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--color-muted);
    flex: none;
  }
  .close-btn:hover { background: var(--color-surface-soft); color: var(--color-ink); }
  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: var(--color-canvas);
    color: var(--color-ink);
    border: 1px solid var(--color-hairline);
    border-radius: 9999px;
    padding: 0.5rem 0.875rem;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    min-height: 36px;
    transition: background 100ms ease, border-color 100ms ease;
  }
  .chip:hover:not(:disabled):not(.active) {
    background: var(--color-surface-soft);
  }
  .chip.active {
    background: var(--color-ink);
    color: var(--color-on-dark);
    border-color: var(--color-ink);
  }
  .chip.active:hover:not(:disabled) {
    background: var(--color-body);
    border-color: var(--color-body);
  }
  .chip:disabled { opacity: 0.5; cursor: wait; }
  .empty {
    margin: 0;
    color: var(--color-muted);
    font-size: 14px;
  }
  .err {
    margin: 0;
    font-size: 13px;
    color: var(--color-error);
  }
  .new-group-btn {
    align-self: flex-start;
    padding: 0.5rem 0.75rem;
  }
</style>
